import { Camera, Mail, Receipt, Ruler, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NALEZ_STAVY, NALEZ_ZDROJE, type NalezStav, type NalezZdroj } from '@helfr/domain';
import { api, hlaskaZChyby } from '../api/client.js';
import { useVybranaZakazka } from '../lib/globalniZakazka.js';
import type {
  NalezFronta,
  NalezyOdpoved,
  Priloha,
  VyzvaAdresat,
  Zakazka,
  ZmenaDetail,
  ZmenaSeznam,
} from '../api/types.js';
import { useAuth } from '../auth/AuthContext.js';
import {
  NALEZ_STAV_BARVA,
  NALEZ_STAV_LABEL,
  NALEZ_ZDROJ_LABEL,
  ZMENA_STAV_LABEL,
} from '../lib/labels.js';
import { formatDatum } from '../lib/format.js';
import {
  Badge,
  Btn,
  Chyba,
  Dialog,
  EmptyState,
  Menu,
  Nacitani,
  Napoveda,
  Pole,
  Select,
  SkeletonSeznam,
  Textarea,
} from '../components/ui.js';
import { ProcesViceprace } from '../components/ProcesViceprace.js';
import { sledujUdalost } from '../lib/analytika.js';

/**
 * NÁLEZY — přípravář (wireframe 13 §2, blueprint 16 §2). Karta = vše pro
 * rozhodnutí bez prokliku (foto, přepis, objekt, autor, stáří). JEDNO primární
 * rozhodnutí „Je to vícepráce", zbytek (Zamítnout, Sloučit) do „⋯ Další akce". Filtry jsou
 * schované za „Filtr ▾" (default „Nové") — nováček neřeší tři selecty nad jedním
 * nálezem. Fronta se vyprazdňuje — řazení nejstarší nezpracované nahoru (API),
 * badge hlídače stáří žlutě/červeně. Nic z funkce nezmizelo, jen z očí.
 */
export function NalezyPage() {
  const [stav, setStav] = useState<NalezStav | ''>('novy');
  const [zdroj, setZdroj] = useState<NalezZdroj | ''>('');
  // Fronta se řídí globálním přepínačem zakázky (lišta nahoře) — lokální filtr
  // v „Filtr ▾" ho umí přepsat (např. „všechny zakázky"), přepnutí v liště
  // přepis ruší (volba v liště je vždy silnější).
  const [prepisZakazky, setPrepisZakazky] = useState<string | null>(null);
  // Inbox (master–detail): vybraný nález; default = první ve frontě (nejstarší
  // nezpracovaný — API řadí frontu, přípravář odbavuje shora).
  const [vybranyId, setVybranyId] = useState<string | null>(null);
  const [akce, setAkce] = useState<{
    typ: 'kvalifikovat' | 'zamitnout' | 'sloucit';
    nalez: NalezFronta;
  } | null>(null);

  const { data: zakazky } = useQuery({
    queryKey: ['zakazky'],
    queryFn: () => api.get<Zakazka[]>('/api/projects'),
  });
  const { vybranaId: globalniZakazkaId } = useVybranaZakazka(zakazky);
  const zakazkaId = prepisZakazky ?? globalniZakazkaId;
  useEffect(() => setPrepisZakazky(null), [globalniZakazkaId]);

  const params = new URLSearchParams();
  if (stav) params.set('stav', stav);
  if (zdroj) params.set('zdroj', zdroj);
  if (zakazkaId) params.set('zakazkaId', zakazkaId);
  const dotaz = params.toString();

  const { data, isLoading, error } = useQuery({
    queryKey: ['nalezy', dotaz],
    queryFn: () => api.get<NalezyOdpoved>(`/api/nalezy${dotaz ? `?${dotaz}` : ''}`),
  });

  // Filtry jsou pokročilé — schované za „Filtr ▾" (default „Nové"). Cesta zpět
  // jedním ťuknutím; nováček nad jedním nálezem neřeší tři selecty (blueprint 16).
  const filtrAktivni = stav !== 'novy' || zdroj !== '' || prepisZakazky !== null;

  // Vybraný detail: držíme volbu, dokud je nález ve výsledku; jinak první.
  const aktivniId =
    vybranyId && data?.nalezy.some((n) => n.id === vybranyId)
      ? vybranyId
      : (data?.nalezy[0]?.id ?? null);
  const vybrany = data?.nalezy.find((n) => n.id === aktivniId);

  return (
    <>
      <ProcesViceprace krok={1} />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">Nálezy</h1>
          <Napoveda titulek="Co jsou nálezy">
            <p>
              Nálezy jsou hlášení z terénu a podněty z kanceláře — „něco nesedí". U každého
              rozhodnete, jestli je to <strong>vícepráce</strong> (založí se Změna), nebo ho
              zamítnete.
            </p>
          </Napoveda>
        </div>
        <Menu
          stitek={`Filtr ${filtrAktivni ? '(aktivní) ' : ''}▾`}
          align="right"
          zavriPoKliku={false}
          spoust="inline-flex min-h-11 cursor-pointer items-center gap-1 rounded-md border border-border-strong bg-surface px-3 text-base font-medium text-text-primary transition-colors duration-100 hover:bg-bg"
        >
          <label className="text-sm font-semibold text-text-secondary">
            Stav
            <Select
              className="mt-1 !min-h-11 w-56"
              value={stav}
              onChange={(e) => setStav(e.target.value as NalezStav | '')}
            >
              <option value="">vše</option>
              {NALEZ_STAVY.map((s) => (
                <option key={s} value={s}>
                  {NALEZ_STAV_LABEL[s]}
                </option>
              ))}
            </Select>
          </label>
          <label className="text-sm font-semibold text-text-secondary">
            Zdroj
            <Select
              className="mt-1 !min-h-11 w-56"
              value={zdroj}
              onChange={(e) => setZdroj(e.target.value as NalezZdroj | '')}
            >
              <option value="">vše</option>
              {NALEZ_ZDROJE.map((z) => (
                <option key={z} value={z}>
                  {NALEZ_ZDROJ_LABEL[z]}
                </option>
              ))}
            </Select>
          </label>
          <label className="text-sm font-semibold text-text-secondary">
            Zakázka
            <Select
              className="mt-1 !min-h-11 w-56"
              value={zakazkaId}
              onChange={(e) => setPrepisZakazky(e.target.value)}
            >
              <option value="">všechny</option>
              {(zakazky ?? []).map((z) => (
                <option key={z.id} value={z.id}>
                  {z.nazev}
                </option>
              ))}
            </Select>
          </label>
        </Menu>
      </div>

      {isLoading && <SkeletonSeznam radku={3} vyska="h-44" />}
      {error ? <Chyba>{hlaskaZChyby(error)}</Chyba> : null}

      {data && data.nalezy.length === 0 && (
        <EmptyState
          ikona={<Search size={22} strokeWidth={1.75} />}
          titulek={zakazkaId ? 'Fronta vybrané zakázky je prázdná' : 'Fronta je prázdná'}
          popis="Nové nálezy z terénu se objeví tady — u každého rozhodnete, jestli je to vícepráce."
          akce={
            zakazkaId ? (
              <button
                type="button"
                className="font-semibold text-primary underline underline-offset-2"
                onClick={() => setPrepisZakazky('')}
              >
                Zobrazit nálezy všech zakázek
              </button>
            ) : undefined
          }
        />
      )}

      {/* Inbox: seznam vlevo (kompaktní řádky s věkem a zdrojem), plný detail
          s rozhodnutím vpravo — přípravář odbavuje frontu shora bez skákání. */}
      {data && data.nalezy.length > 0 && (
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(280px,340px)_minmax(0,1fr)]">
          <div className="flex flex-col gap-1.5">
            {data.nalezy.map((n) => (
              <NalezRadek
                key={n.id}
                nalez={n}
                vybrany={n.id === aktivniId}
                onVybrat={() => setVybranyId(n.id)}
              />
            ))}
          </div>
          {vybrany && (
            <NalezKarta
              nalez={vybrany}
              prahDni={data.prahDni}
              onAkce={(typ) => setAkce({ typ, nalez: vybrany })}
            />
          )}
        </div>
      )}

      {akce?.typ === 'kvalifikovat' && (
        <KvalifikovatDialog
          nalez={akce.nalez}
          zakazky={zakazky ?? []}
          onZavrit={() => setAkce(null)}
        />
      )}
      {akce?.typ === 'zamitnout' && (
        <ZamitnoutDialog nalez={akce.nalez} onZavrit={() => setAkce(null)} />
      )}
      {akce?.typ === 'sloucit' && (
        <SloucitDialog nalez={akce.nalez} onZavrit={() => setAkce(null)} />
      )}
    </>
  );
}

/** Odstraní obalové uvozovky z přepisu, aby karta neobalovala uvozovkami text,
 * který už uvozovky má (UX audit B2.4). */
export function bezUvozovek(text: string): string {
  return text
    .replace(/^[„"“”]+/, '')
    .replace(/[„"“”]+$/, '')
    .trim();
}

/** Náhledy příloh nálezu: fotky miniaturou, audio odkazem na přehrání. */
export function PrilohyNalezu({ prilohy }: { prilohy: Priloha[] }) {
  if (prilohy.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {prilohy.map((p) =>
        p.mimeType?.startsWith('image/') ? (
          <img
            key={p.id}
            src={`/api/diary/prilohy/${p.id}/soubor`}
            alt={p.nazev}
            className="h-20 w-24 rounded-lg border border-border object-cover"
          />
        ) : (
          <a
            key={p.id}
            href={`/api/diary/prilohy/${p.id}/soubor`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-11 items-center rounded-lg border border-border-strong px-3 text-sm font-medium text-primary hover:bg-bg"
          >
            {p.mimeType?.startsWith('audio/') ? '▶ přehrát audio' : p.nazev}
          </a>
        ),
      )}
    </div>
  );
}

/**
 * Řádek inboxu (master seznam): zdroj + objekt, popis na 2 řádky, autor/datum
 * a věkový badge. Vybraný = navy hrana + tint. Rozhodnutí se dělá v detailu.
 */
function NalezRadek({
  nalez,
  vybrany,
  onVybrat,
}: {
  nalez: NalezFronta;
  vybrany: boolean;
  onVybrat: () => void;
}) {
  return (
    <button
      type="button"
      aria-current={vybrany}
      onClick={onVybrat}
      className={`flex w-full flex-col gap-1 rounded-lg border p-3 text-left transition-colors duration-100 ${
        vybrany
          ? 'border-primary bg-primary-tint shadow-sm'
          : 'border-border bg-surface hover:border-border-strong hover:bg-bg'
      }`}
    >
      <span className="flex items-center gap-1.5 text-xs font-semibold text-text-secondary">
        <span
          aria-hidden="true"
          className="flex size-5 items-center justify-center rounded bg-primary-tint text-primary"
        >
          <ZdrojIkona zdroj={nalez.zdroj} />
        </span>
        <span className="min-w-0 truncate">{NALEZ_ZDROJ_LABEL[nalez.zdroj]}</span>
        {nalez.objekt && <span className="shrink-0">· {nalez.objekt.kod}</span>}
      </span>
      <span className="line-clamp-2 text-sm font-medium text-text-primary">
        „{bezUvozovek(nalez.popis)}“
      </span>
      <span className="flex flex-wrap items-center gap-2 text-xs text-text-secondary">
        {nalez.autor.jmeno} · {formatDatum(nalez.kdy)}
        {nalez.eskalace !== 'ok' && (
          <Badge stav={nalez.eskalace === 'cervena' ? 'error' : 'warning'}>
            {nalez.stariPracovnichDni} dní bez změny
          </Badge>
        )}
        {nalez.stav !== 'novy' && (
          <Badge barva={NALEZ_STAV_BARVA[nalez.stav]}>{NALEZ_STAV_LABEL[nalez.stav]}</Badge>
        )}
      </span>
    </button>
  );
}

/** SVG ikona zdroje nálezu (dřív emoji v labels.ts). */
function ZdrojIkona({ zdroj }: { zdroj: NalezZdroj }) {
  const spolecne = { size: 14, strokeWidth: 2 } as const;
  if (zdroj === 'teren') return <Camera {...spolecne} />;
  if (zdroj === 'revize_pd') return <Ruler {...spolecne} />;
  if (zdroj === 'pozadavek_investora') return <Mail {...spolecne} />;
  return <Receipt {...spolecne} />;
}

function NalezKarta({
  nalez,
  prahDni,
  onAkce,
}: {
  nalez: NalezFronta;
  prahDni: number;
  onAkce: (typ: 'kvalifikovat' | 'zamitnout' | 'sloucit') => void;
}) {
  return (
    <section className="rounded-xl border border-border bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-sm text-text-secondary">
          <span
            aria-hidden="true"
            className="flex size-6 items-center justify-center rounded-md bg-primary-tint text-primary"
          >
            <ZdrojIkona zdroj={nalez.zdroj} />
          </span>
          <span className="font-semibold">{NALEZ_ZDROJ_LABEL[nalez.zdroj]}</span>
          {nalez.objekt && <span>· {nalez.objekt.kod}</span>}
          <span>· {nalez.autor.jmeno}</span>
          <span>· {formatDatum(nalez.kdy)}</span>
          {nalez.stav !== 'novy' && (
            <Badge barva={NALEZ_STAV_BARVA[nalez.stav]}>{NALEZ_STAV_LABEL[nalez.stav]}</Badge>
          )}
        </div>
        {nalez.eskalace !== 'ok' && (
          <Badge stav={nalez.eskalace === 'cervena' ? 'error' : 'warning'}>
            ⚠ {nalez.stariPracovnichDni} dní bez změny (práh {prahDni})
          </Badge>
        )}
      </div>

      <div className="mt-3 flex flex-col gap-3">
        <PrilohyNalezu prilohy={nalez.prilohy} />
        <p className="text-base">
          <span className="text-text-secondary">Přepis/popis: </span>„{bezUvozovek(nalez.popis)}"
        </p>
        {!nalez.objekt && (
          <p className="text-sm text-text-secondary">
            Bez objektu (mistr přeskočil) — kvalifikaci to neblokuje.
          </p>
        )}
        <div className="flex flex-wrap gap-3 text-sm text-text-secondary">
          {nalez.denikZaznam && (
            <Link to={`/denik/${nalez.denikZaznam.id}`} className="underline underline-offset-2">
              Vazba: deníkový záznam {formatDatum(nalez.denikZaznam.datum)} (read-only)
            </Link>
          )}
          {nalez.drawingVersion && (
            <Link
              to={`/vykresy?vykres=${nalez.drawingVersion.drawing.id}`}
              className="underline underline-offset-2"
            >
              Vazba: výkres {nalez.drawingVersion.drawing.cislo} v{nalez.drawingVersion.verze}
            </Link>
          )}
          {nalez.zmena && (
            <Link to={`/zmeny/${nalez.zmena.id}`} className="underline underline-offset-2">
              Změna {nalez.zmena.cislo ?? ''} ({ZMENA_STAV_LABEL[nalez.zmena.stav]})
            </Link>
          )}
          {nalez.stav === 'zamitnut' && nalez.duvodZamitnuti && (
            <span className="text-error-text">Důvod zamítnutí: {nalez.duvodZamitnuti}</span>
          )}
        </div>
      </div>

      {nalez.stav === 'novy' && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {/* JEDNA primární akce = rozhodnutí „je to vícepráce?" (blueprint 16). */}
          <Btn size="desktop" onClick={() => onAkce('kvalifikovat')}>
            Je to vícepráce
          </Btn>
          <Btn size="desktop" varianta="secondary" onClick={() => onAkce('zamitnout')}>
            Zamítnout
          </Btn>
          {/* Vzácné akce (sloučení) schované do „⋯ Další akce" (blueprint 16). */}
          <Menu stitek="⋯ Další akce">
            <Btn size="desktop" varianta="secondary" onClick={() => onAkce('sloucit')}>
              Sloučit s…
            </Btn>
          </Menu>
        </div>
      )}
    </section>
  );
}

/**
 * „JE TO VÍCEPRÁCE" (kvalifikace nálezu na Změnu): popis (z přepisu), objekt
 * a kvalifikující dvojice stavbyvedoucí + přípravář (07 §9) předvyplněné;
 * potvrzení → redirect na detail Změny. Nález se needituje — je to důkaz.
 */
function KvalifikovatDialog({
  nalez,
  zakazky,
  onZavrit,
}: {
  nalez: NalezFronta;
  zakazky: Zakazka[];
  onZavrit: () => void;
}) {
  const { me, maPravo } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [popis, setPopis] = useState(nalez.popis);
  const [objektId, setObjektId] = useState(nalez.objektId ?? '');
  // Přihlášený se předvyplní do své role (denik.sign → stavbyvedoucí, jinak přípravář).
  const prihlasenyJeStavbyvedouci = maPravo('denik.sign');
  const [stavbyvedouciId, setStavbyvedouciId] = useState(
    prihlasenyJeStavbyvedouci ? (me?.userId ?? '') : '',
  );
  const [pripravarId, setPripravarId] = useState(
    prihlasenyJeStavbyvedouci ? '' : (me?.userId ?? ''),
  );
  const [chyba, setChyba] = useState<string | null>(null);

  const { data: lide } = useQuery({
    queryKey: ['adresati'],
    queryFn: () => api.get<VyzvaAdresat[]>('/api/vyzvy/adresati'),
  });
  // Nabízet lidi PODLE ROLE (90 % případů) — TDI ani investor kvalifikaci
  // nedělají. Role jsou konfig per firma → hledá se podle názvu; když filtr
  // nic nenajde (přejmenované role), spadne na všechny.
  const podleRole = (klicova: string[]): VyzvaAdresat[] => {
    const uzsi = (lide ?? []).filter((u) =>
      u.role.some((r) => klicova.some((k) => r.toLowerCase().includes(k))),
    );
    return uzsi.length > 0 ? uzsi : (lide ?? []);
  };
  const stavbyvedouci = podleRole(['stavbyved', 'mistr']);
  const pripravari = podleRole(['příprav', 'priprav', 'ředitel', 'reditel']);
  const objekty = zakazky.find((z) => z.id === nalez.zakazkaId)?.objekty ?? [];

  const kvalifikovat = useMutation({
    mutationFn: () =>
      api.post<ZmenaDetail>(`/api/nalezy/${nalez.id}/kvalifikovat`, {
        popis,
        ...(objektId ? { objektId } : { objektId: null }),
        ...(stavbyvedouciId && { kvalifikovalStavbyvedouciId: stavbyvedouciId }),
        ...(pripravarId && { kvalifikovalPripravarId: pripravarId }),
      }),
    onSuccess: (zmena) => {
      sledujUdalost('nalez_rozhodnut', { vysledek: 'viceprace' });
      void queryClient.invalidateQueries({ queryKey: ['nalezy'] });
      void navigate(`/zmeny/${zmena.id}`);
    },
    onError: (err) => setChyba(hlaskaZChyby(err)),
  });

  return (
    <Dialog nadpis="Je to vícepráce" onZavrit={onZavrit}>
      <div className="flex flex-col gap-4">
        {chyba && <Chyba>{chyba}</Chyba>}
        <p className="text-sm text-text-secondary">
          Potvrzujete, že je to vícepráce — dělá to stavbyvedoucí s přípravářem společně. Ocenění je
          další krok, v detailu Změny.
        </p>
        <Pole popisek="Popis Změny" povinne>
          <Textarea
            aria-label="Popis Změny"
            rows={3}
            value={popis}
            onChange={(e) => setPopis(e.target.value)}
          />
        </Pole>
        <Pole popisek="Objekt">
          <Select
            aria-label="Objekt Změny"
            value={objektId}
            onChange={(e) => setObjektId(e.target.value)}
          >
            <option value="">(bez objektu)</option>
            {objekty.map((o) => (
              <option key={o.id} value={o.id}>
                {o.kod} — {o.nazev}
              </option>
            ))}
          </Select>
        </Pole>
        <div className="grid gap-4 sm:grid-cols-2">
          <Pole popisek="Kvalifikoval — stavbyvedoucí">
            <Select
              aria-label="Kvalifikoval — stavbyvedoucí"
              value={stavbyvedouciId}
              onChange={(e) => setStavbyvedouciId(e.target.value)}
            >
              <option value="">(nevybráno)</option>
              {stavbyvedouci.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.jmeno}
                </option>
              ))}
            </Select>
          </Pole>
          <Pole popisek="Kvalifikoval — přípravář">
            <Select
              aria-label="Kvalifikoval — přípravář"
              value={pripravarId}
              onChange={(e) => setPripravarId(e.target.value)}
            >
              <option value="">(nevybráno)</option>
              {pripravari.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.jmeno}
                </option>
              ))}
            </Select>
          </Pole>
        </div>
        <div className="flex gap-3">
          <Btn
            size="desktop"
            disabled={kvalifikovat.isPending || popis.trim() === ''}
            onClick={() => kvalifikovat.mutate()}
          >
            {kvalifikovat.isPending ? 'Zakládám…' : 'Založit Změnu'}
          </Btn>
          <Btn size="desktop" varianta="secondary" onClick={onZavrit}>
            Zrušit
          </Btn>
        </div>
      </div>
    </Dialog>
  );
}

/** ZAMÍTNOUT — důvod povinný: uvidí ho mistr; bez zpětné vazby přestane hlásit. */
function ZamitnoutDialog({ nalez, onZavrit }: { nalez: NalezFronta; onZavrit: () => void }) {
  const queryClient = useQueryClient();
  const [duvod, setDuvod] = useState('');
  const [chyba, setChyba] = useState<string | null>(null);

  const zamitnout = useMutation({
    mutationFn: () => api.post(`/api/nalezy/${nalez.id}/zamitnout`, { duvod }),
    onSuccess: () => {
      sledujUdalost('nalez_rozhodnut', { vysledek: 'zamitnut' });
      void queryClient.invalidateQueries({ queryKey: ['nalezy'] });
      onZavrit();
    },
    onError: (err) => setChyba(hlaskaZChyby(err)),
  });

  return (
    <Dialog nadpis="Zamítnout nález" onZavrit={onZavrit}>
      <div className="flex flex-col gap-4">
        {chyba && <Chyba>{chyba}</Chyba>}
        <Pole popisek="Důvod zamítnutí" povinne>
          <Textarea
            aria-label="Důvod zamítnutí"
            rows={3}
            placeholder="Např. je to v rozsahu SOD, položka 762-01…"
            value={duvod}
            onChange={(e) => setDuvod(e.target.value)}
          />
        </Pole>
        <p className="text-sm text-warning-text">
          Důvod uvidí mistr u svého nálezu — bez zpětné vazby přestane hlásit.
        </p>
        <div className="flex gap-3">
          <Btn
            size="desktop"
            varianta="danger"
            disabled={zamitnout.isPending || duvod.trim().length < 3}
            onClick={() => zamitnout.mutate()}
          >
            {zamitnout.isPending ? 'Zamítám…' : 'Zamítnout nález'}
          </Btn>
          <Btn size="desktop" varianta="secondary" onClick={onZavrit}>
            Zrušit
          </Btn>
        </div>
      </div>
    </Dialog>
  );
}

/** SLOUČIT S… — nález se přivěsí k existující Změně zakázky jako další podklad. */
function SloucitDialog({ nalez, onZavrit }: { nalez: NalezFronta; onZavrit: () => void }) {
  const queryClient = useQueryClient();
  const [zmenaId, setZmenaId] = useState('');
  const [chyba, setChyba] = useState<string | null>(null);

  const { data: zmeny, isLoading } = useQuery({
    queryKey: ['zmeny', nalez.zakazkaId],
    queryFn: () => api.get<ZmenaSeznam[]>(`/api/zmeny?zakazkaId=${nalez.zakazkaId}`),
  });

  const sloucit = useMutation({
    mutationFn: () => api.post(`/api/nalezy/${nalez.id}/sloucit`, { zmenaId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['nalezy'] });
      onZavrit();
    },
    onError: (err) => setChyba(hlaskaZChyby(err)),
  });

  return (
    <Dialog nadpis="Sloučit s existující Změnou" onZavrit={onZavrit}>
      <div className="flex flex-col gap-4">
        {chyba && <Chyba>{chyba}</Chyba>}
        {isLoading && <Nacitani text="Načítám Změny zakázky…" />}
        {zmeny && zmeny.length === 0 && (
          <p className="text-text-secondary">
            Zakázka zatím nemá žádnou Změnu — nález nejde sloučit, kvalifikujte ho.
          </p>
        )}
        {zmeny && zmeny.length > 0 && (
          <Pole popisek="Existující Změna zakázky" povinne>
            <Select
              aria-label="Existující Změna zakázky"
              value={zmenaId}
              onChange={(e) => setZmenaId(e.target.value)}
            >
              <option value="">(vyberte Změnu)</option>
              {zmeny.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.cislo ?? z.id} — {z.popis} ({ZMENA_STAV_LABEL[z.stav]})
                </option>
              ))}
            </Select>
          </Pole>
        )}
        <p className="text-sm text-text-secondary">
          Nález zůstane u Změny jako další podklad (důkaz se nezničí) — žádné duplicitní Změny.
        </p>
        <div className="flex gap-3">
          <Btn
            size="desktop"
            disabled={sloucit.isPending || zmenaId === ''}
            onClick={() => sloucit.mutate()}
          >
            {sloucit.isPending ? 'Slučuji…' : 'Sloučit'}
          </Btn>
          <Btn size="desktop" varianta="secondary" onClick={onZavrit}>
            Zrušit
          </Btn>
        </div>
      </div>
    </Dialog>
  );
}
